/* ============================================================================
   Mock data — rewards, gamification, missions, dashboard and the demo user.
   The demo account name is deliberately long (28 characters) so truncation and
   avatar initials are tested with real content, not "John".
   ========================================================================== */

import type {
  ActivityEntry,
  DashboardSummary,
  Mission,
  RewardAccount,
  RewardOffer,
  User,
} from '@core/types'
import { isoFromNow, isoMonthsAgo } from './climate'

export const USER: User = {
  id: 'us-1001',
  displayName: 'Zainab Abd Al-Rahman Al-Khafaji',
  email: 'zainab.alkhafaji@example.iq',
  phone: '0770 123 4567',
  governorate: 'Baghdad',
  district: 'Al-Adhamiyah',
  role: 'user',
  language: 'en',
  createdAt: isoMonthsAgo(33, 4),
  plantsCount: 6,
  notificationPrefs: {
    careReminders: true,
    missionInvites: true,
    marketplaceOffers: false,
    weeklyImpactEmail: true,
  },
}

export const REWARD_ACCOUNT: RewardAccount = {
  pointsBalance: 1_845,
  lifetimePoints: 4_820,
  tier: 'grove',
  nextTierAt: 6_000,
  currentStreakDays: 12,
  longestStreakDays: 41,
  history: [
    {
      id: 'pt-9001',
      label: 'Verification photo approved — Nakhla date palm',
      points: 30,
      occurredAt: isoFromNow(-6, 17),
      source: 'care',
    },
    {
      id: 'pt-9002',
      label: 'Mission: monitor 10 street trees on Abu Nuwas Street',
      points: 120,
      occurredAt: isoFromNow(-8, 10),
      source: 'mission',
    },
    {
      id: 'pt-9003',
      label: 'Quiz: salinity-tolerant species (4 of 5 correct)',
      points: 40,
      occurredAt: isoFromNow(-11, 20),
      source: 'quiz',
    },
    {
      id: 'pt-9004',
      label: 'Redeemed: 15% seedling discount at Al-Rafidain Nursery',
      points: -250,
      occurredAt: isoFromNow(-16, 12),
      source: 'redemption',
    },
    {
      id: 'pt-9005',
      label: 'Care task completed — deep water the Sidr',
      points: 20,
      occurredAt: isoFromNow(-18, 7),
      source: 'care',
    },
    {
      id: 'pt-9006',
      label: 'Referral: neighbour joined and planted',
      points: 75,
      occurredAt: isoFromNow(-27, 15),
      source: 'referral',
    },
    {
      id: 'pt-9007',
      label: 'Planting verified — lemon tree in the courtyard',
      points: 150,
      occurredAt: isoMonthsAgo(9, 8),
      source: 'planting',
    },
    {
      id: 'pt-9008',
      label: 'Planting verified — 14 oleander plants, Kufa street hedge',
      points: 180,
      occurredAt: isoMonthsAgo(33, 5),
      source: 'planting',
    },
  ],
}

export const REWARD_OFFERS: RewardOffer[] = [
  {
    id: 'of-4001',
    title: '15% off seedlings and fruit trees',
    partnerName: 'Al-Rafidain Nursery',
    partnerType: 'nursery',
    costPoints: 500,
    discountLabel: '15% discount',
    locality: 'Baghdad, Al-Adhamiyah',
    expiresAt: isoFromNow(64),
    category: 'plants',
    redeemed: true,
  },
  {
    id: 'of-4002',
    title: '10,000 IQD voucher on purchases over 40,000 IQD',
    partnerName: 'Basra Garden Centre',
    partnerType: 'nursery',
    costPoints: 750,
    discountLabel: '10,000 IQD off',
    locality: 'Basra, Al-Jubaila',
    expiresAt: isoFromNow(45),
    category: 'plants',
  },
  {
    id: 'of-4003',
    title: 'Free delivery for orders above 50,000 IQD',
    partnerName: 'Dijlah Agriculture Supplies',
    partnerType: 'supplier',
    costPoints: 300,
    discountLabel: 'Free delivery',
    locality: 'Baghdad and Babil',
    expiresAt: isoFromNow(90),
    category: 'delivery',
  },
  {
    id: 'of-4004',
    title: '20% off composted soil, 50 kg bags',
    partnerName: 'Najaf Growers Cooperative',
    partnerType: 'supplier',
    costPoints: 400,
    discountLabel: '20% discount',
    locality: 'Najaf, Kufa',
    expiresAt: isoFromNow(38),
    category: 'soil',
  },
  {
    id: 'of-4005',
    title: 'Soil salinity testing kit for one season',
    partnerName: 'Mesopotamia Soil Lab',
    partnerType: 'supplier',
    costPoints: 900,
    discountLabel: 'Kit on loan',
    locality: 'Baghdad, Karrada',
    expiresAt: isoFromNow(120),
    category: 'tools',
  },
  {
    id: 'of-4006',
    title: 'Online course: managing soil salinity in Iraq',
    partnerName: 'EcoNinhursag Academy',
    partnerType: 'supplier',
    costPoints: 600,
    discountLabel: 'Course access',
    locality: 'Online, self-paced',
    expiresAt: isoFromNow(180),
    category: 'course',
  },
]

/* --------------------------------------------------------------- missions --
   Missions are the "community aspect" from the concept notes: clean, monitor,
   plant and learn activities that reward every contribution. */

export const MISSIONS: Mission[] = [
  {
    id: 'ms-5001',
    title: 'Clean and re-mulch the Karrada school garden',
    description:
      'Forty volunteers are needed for one Saturday morning. Bring gloves if you have them; bags, rakes and mulch are provided on site.',
    category: 'clean',
    locationLabel: 'Baghdad, Karrada — Al-Mansour Primary School',
    startsAt: isoFromNow(6, 8),
    endsAt: isoFromNow(6, 13),
    rewardPoints: 120,
    participants: 26,
    capacity: 40,
    proofRequired: true,
    status: 'open',
  },
  {
    id: 'ms-5002',
    title: 'Monitor 10 street trees on Abu Nuwas Street',
    description:
      'Walk the 2 km stretch, photograph each tree, and flag any with damaged irrigation or dead branches. Takes about 90 minutes.',
    category: 'monitor',
    locationLabel: 'Baghdad, Al-Karkh — Abu Nuwas Street',
    startsAt: isoFromNow(-8, 9),
    endsAt: isoFromNow(9, 18),
    rewardPoints: 90,
    participants: 12,
    capacity: 20,
    proofRequired: true,
    status: 'joined',
  },
  {
    id: 'ms-5003',
    title: 'November planting drive — 300 Sidr seedlings',
    description:
      'The largest campaign of the year in Muthanna. Transport from Al-Samawah centre is arranged for volunteers without a car.',
    category: 'plant',
    locationLabel: 'Muthanna, Al-Samawah — eastern shelterbelt',
    startsAt: isoFromNow(41, 7),
    endsAt: isoFromNow(43, 16),
    rewardPoints: 250,
    participants: 88,
    capacity: 300,
    proofRequired: true,
    status: 'open',
  },
  {
    id: 'ms-5004',
    title: 'Report leaking irrigation lines in Kufa (round 2)',
    description:
      'The first round found 17 leaks along the main canal. This round covers the residential streets west of the old market.',
    category: 'monitor',
    locationLabel: 'Najaf, Kufa — west of the old market',
    startsAt: isoFromNow(13, 8),
    endsAt: isoFromNow(20, 18),
    rewardPoints: 100,
    participants: 9,
    capacity: 30,
    proofRequired: false,
    status: 'open',
  },
  {
    id: 'ms-5005',
    title: 'Learn: salinity tolerance quiz',
    description:
      'Five questions on which species survive saline irrigation. Completing it unlocks the advanced soil module.',
    category: 'learn',
    locationLabel: 'Online',
    startsAt: isoFromNow(-11, 8),
    endsAt: isoFromNow(-11, 23),
    rewardPoints: 40,
    participants: 1_284,
    capacity: 5_000,
    proofRequired: false,
    status: 'completed',
  },
  {
    id: 'ms-5006',
    title: 'Clean the Shatt Al-Arab canal bank',
    description:
      'Completed in May with 240 volunteers and 11 tonnes of collected waste. The report is available in the campaign archive.',
    category: 'clean',
    locationLabel: 'Basra, Shatt Al-Arab — north bank',
    startsAt: isoMonthsAgo(4, 16),
    endsAt: isoMonthsAgo(4, 16),
    rewardPoints: 130,
    participants: 240,
    capacity: 240,
    proofRequired: true,
    status: 'closed',
  },
]

/* -------------------------------------------------------------- dashboard -- */

export const DASHBOARD_SUMMARY: DashboardSummary = {
  plants: { total: 6, thriving: 3, needsWater: 1, atRisk: 1, lost: 1 },
  survivalRate: 0.833,
  verifiedActions: 47,
  pointsBalance: 1_845,
  streakDays: 12,
  monthlyImpact: buildMonthlyImpact(),
}

function buildMonthlyImpact(): DashboardSummary['monthlyImpact'] {
  const planted = [4, 6, 5, 9, 12, 8, 11, 14, 16, 13, 18, 21]
  const verified = [3, 5, 5, 8, 11, 7, 10, 12, 14, 12, 16, 18]
  const surviving = [3, 5, 4, 7, 10, 6, 9, 11, 13, 11, 15, 17]
  const now = new Date()
  return planted.map((count, index) => {
    const date = new Date(now)
    date.setMonth(now.getMonth() - (planted.length - 1 - index))
    const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    return {
      month: iso,
      label: new Intl.DateTimeFormat('en', { month: 'short' }).format(date),
      planted: count,
      verified: verified[index],
      surviving: surviving[index],
    }
  })
}

export const ACTIVITY: ActivityEntry[] = [
  {
    id: 'ac-7001',
    kind: 'care-completed',
    title: 'Deep water the Sidr',
    detail: 'Sidr by the school wall · 40 litres in a slow basin',
    occurredAt: isoFromNow(-1, 7),
    points: 20,
  },
  {
    id: 'ac-7002',
    kind: 'verified',
    title: 'Verification photo approved',
    detail: 'Nakhla — date palm behind the house',
    occurredAt: isoFromNow(-6, 17),
    points: 30,
  },
  {
    id: 'ac-7003',
    kind: 'mission',
    title: 'Mission joined: monitor 10 street trees',
    detail: 'Baghdad, Al-Karkh — Abu Nuwas Street',
    occurredAt: isoFromNow(-8, 9),
    points: 0,
  },
  {
    id: 'ac-7004',
    kind: 'disease-scan',
    title: 'Disease scan completed',
    detail: 'Graphiola leaf spot on Nakhla date palm · 87% confidence',
    occurredAt: isoFromNow(-30, 11),
    points: 0,
  },
  {
    id: 'ac-7005',
    kind: 'redemption',
    title: 'Reward redeemed',
    detail: '15% seedling discount at Al-Rafidain Nursery',
    occurredAt: isoFromNow(-16, 12),
    points: -250,
  },
  {
    id: 'ac-7006',
    kind: 'planted',
    title: 'Lemon tree registered',
    detail: 'Erbil, Ainkawa — courtyard planting',
    occurredAt: isoMonthsAgo(9, 8),
    points: 150,
  },
  {
    id: 'ac-7007',
    kind: 'care-completed',
    title: 'Loss confirmed and replacement planned',
    detail: 'Athel windbreak row, Al-Samawah — pump failure in July',
    occurredAt: isoFromNow(-48, 10),
    points: 15,
  },
  {
    id: 'ac-7008',
    kind: 'mission',
    title: 'Mission completed: salinity quiz',
    detail: 'Score 4 of 5 · advanced soil module unlocked',
    occurredAt: isoFromNow(-11, 20),
    points: 40,
  },
]
